import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getSiteName, getSiteInitial } from '../utils/theme';

const Footer: React.FC = () => {
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleUnsubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/unsubscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitMessage('Запрос на отмену подписки отправлен.');
        setEmail('');
      } else {
        setSubmitMessage('Произошла ошибка. Попробуйте еще раз или свяжитесь с поддержкой.');
      }
    } catch (error) {
      setSubmitMessage('Произошла ошибка. Попробуйте еще раз или свяжитесь с поддержкой.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <footer className="bg-lunar-900 border-t border-[var(--primary-color-800-30)] py-12 px-4">
      <div className="container mx-auto">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-mystic-gradient rounded-full flex items-center justify-center">
                <span className="text-white font-elegant text-lg">{getSiteInitial()}</span>
              </div>
              <span className="text-2xl font-elegant font-bold text-white">{getSiteName()}</span>
            </div>
            <p className="text-lunar-400 text-sm leading-relaxed mb-4">
              Умный ИИ-помощник. Автоматизируйте создание контента с помощью передовых технологий искусственного интеллекта.
            </p>
            <button
              onClick={() => setIsUnsubscribeModalOpen(true)}
              className="bg-transparent border border-[var(--primary-color-600)] text-mystic-300 px-4 py-2 rounded-lg text-sm hover:bg-[var(--primary-color-600-20)] hover:border-[var(--primary-color-500)] transition-all duration-300"
            >
              Отмена подписки
            </button>
          </div>

          {/* Legal Documents */}
          <div>
            <h3 className="text-white font-semibold mb-4">Документы</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy-policy" className="text-lunar-400 hover:text-mystic-400 transition-colors">Политика конфиденциальности</Link></li>
              <li><Link to="/terms-of-service" className="text-lunar-400 hover:text-mystic-400 transition-colors">Пользовательское соглашение</Link></li>
              <li><Link to="/security-policy" className="text-lunar-400 hover:text-mystic-400 transition-colors">Политика безопасности</Link></li>
              <li><Link to="/auto-payments" className="text-lunar-400 hover:text-mystic-400 transition-colors">Соглашение об автоплатежах</Link></li>
              <li><Link to="/tariffs" className="text-lunar-400 hover:text-mystic-400 transition-colors">Тарифы</Link></li>
            </ul>
          </div>

          {/* Legal Information */}
          <div>
            <h3 className="text-white font-semibold mb-4">Юридическая информация</h3>
            <div className="space-y-2 text-sm text-lunar-400">
              <p><strong>{process.env.REACT_APP_COMPANY_NAME}</strong></p>
              <p>ИНН: {process.env.REACT_APP_COMPANY_INN}</p>
              <p>ОГРН: {process.env.REACT_APP_COMPANY_OGRN}</p>
              <p>{process.env.REACT_APP_COMPANY_ADDRESS}</p>
            </div>
          </div>

          {/* Payment Methods */}
          <div>
            <h3 className="text-white font-semibold mb-4">Способы оплаты</h3>
            <div className="flex space-x-3">
              <img src="/visa.svg" alt="Visa" className="h-6 w-auto opacity-80 hover:opacity-100 transition-opacity" />
              <img src="/mastercard.svg" alt="Mastercard" className="h-6 w-auto opacity-80 hover:opacity-100 transition-opacity" />
              <img src="/mir.svg" alt="МИР" className="h-6 w-auto opacity-80 hover:opacity-100 transition-opacity" />
            </div>
            <div className="mt-4 space-y-2 text-sm text-lunar-400">
              <p>{process.env.REACT_APP_SUPPORT_EMAIL}</p>
              <p>{process.env.REACT_APP_SUPPORT_PHONE}</p>
              <p>{process.env.REACT_APP_SUPPORT_HOURS}</p>
            </div>
          </div>
        </div>



        {/* Copyright */}
        <div className="border-t border-[var(--primary-color-800-30)] pt-8 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-lunar-500 text-sm mb-4 md:mb-0">
              © 2025 {getSiteName()}. Все права защищены.
            </p>
            

          </div>
        </div>
      </div>

      {/* Unsubscribe Modal */}
      {isUnsubscribeModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-lunar-800 rounded-2xl p-8 max-w-md w-full border border-[var(--primary-color-800-30)] shadow-2xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-elegant font-bold text-white mb-2">
                Отмена подписки
              </h3>
              <p className="text-lunar-400 text-sm">
                Введите ваш email для отмены подписки
              </p>
            </div>

            <form onSubmit={handleUnsubscribe} className="space-y-4">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ваш email"
                  className="w-full px-4 py-3 bg-lunar-700 border border-[var(--primary-color-800-30)] rounded-lg text-white placeholder-lunar-400 focus:outline-none focus:border-[var(--primary-color-500)] transition-colors"
                  required
                />
              </div>

              {submitMessage && (
                <div className={`text-sm p-3 rounded-lg ${
                  submitMessage.includes('отправлен') 
                    ? 'bg-green-900/30 text-green-400 border border-green-800/30' 
                    : 'bg-red-900/30 text-red-400 border border-red-800/30'
                }`}>
                  {submitMessage}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsUnsubscribeModalOpen(false);
                    setEmail('');
                    setSubmitMessage('');
                  }}
                  className="flex-1 px-4 py-3 bg-lunar-700 text-lunar-300 rounded-lg hover:bg-lunar-600 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !email.trim()}
                  className="flex-1 px-4 py-3 bg-mystic-gradient text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-lunar-400 text-sm">
                Если у Вас возникли проблемы, Вы всегда можете связаться с нашей{' '}
                <a 
                  href="mailto:support@gekata.net" 
                  className="text-mystic-400 hover:text-mystic-300 transition-colors underline"
                >
                  поддержкой
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;